import React, { useState } from "react";

// Teletorget Client Dashboard – Social Media + Blaze.ai integration
// Responsive React canvas

const defaultPlatforms = [
  {
    id: "website",
    label: "Website",
    icon: "🌐",
    placeholder: "https://yourcompany.com",
  },
  {
    id: "facebook",
    label: "Facebook",
    icon: "📘",
    placeholder: "https://facebook.com/yourpage",
  },
  {
    id: "instagram",
    label: "Instagram",
    icon: "📸",
    placeholder: "https://instagram.com/yourpage",
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    icon: "🔗",
    placeholder: "https://linkedin.com/company/yourpage",
  },
  {
    id: "tiktok",
    label: "TikTok",
    icon: "🎵",
    placeholder: "https://tiktok.com/@yourpage",
  },
  {
    id: "x",
    label: "X / Twitter",
    icon: "🐦",
    placeholder: "https://x.com/yourpage",
  },
  {
    id: "youtube",
    label: "YouTube",
    icon: "▶️",
    placeholder: "https://youtube.com/@yourchannel",
  },
];

const workflows = ["Auto-Schedule", "Manual Review", "AI Re-Write"];

function SocialPlatformRow({
  platform,
  onChange,
  onRemove,
  showRemove,
  blazeConnected,
}) {
  const handleFieldChange = (field, value) => {
    onChange({ ...platform, [field]: value });
  };

  const isConnected = Boolean(platform.url && platform.url.trim() !== "");

  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-white/80 shadow-sm p-4 flex flex-col items-center gap-3">
      {/* Logo + name */}
      <div className="flex flex-col items-center gap-2 w-full">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-xl text-white">
          <span>{platform.icon || "🔗"}</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            className="border-none bg-transparent text-sm font-semibold text-slate-900 focus:outline-none focus:ring-0 p-0 text-center"
            value={platform.label}
            onChange={(e) => handleFieldChange("label", e.target.value)}
          />
          {platform.isCustom && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
              Custom
            </span>
          )}
        </div>
        <span
          className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
            isConnected
              ? "bg-emerald-100 text-emerald-700"
              : "bg-slate-100 text-slate-500"
          }`}
        >
          {isConnected ? "Connected" : "Not connected"}
        </span>
      </div>

      {/* URL + Blaze controls */}
      <div className="w-full flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium text-slate-600 text-center sm:text-left">
            Profile URL
          </label>
          <input
            type="url"
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 shadow-inner focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
            placeholder={platform.placeholder || "https://"}
            value={platform.url || ""}
            onChange={(e) => handleFieldChange("url", e.target.value)}
          />
        </div>

        <div className="grid w-full gap-3 text-[11px] text-slate-500 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <span className="font-medium text-slate-700">Blaze.ai</span>
            <p className="text-[10px] text-slate-500">
              {blazeConnected
                ? "Enable AI publishing for this channel."
                : "Connect Blaze.ai to unlock automation."}
            </p>
          </div>
          <div className="flex flex-col items-center sm:items-end gap-1">
            <button
              type="button"
              disabled={!blazeConnected}
              onClick={() =>
                handleFieldChange("blazeEnabled", !platform.blazeEnabled)
              }
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-1 ${
                platform.blazeEnabled && blazeConnected
                  ? "bg-sky-500"
                  : "bg-slate-200"
              } ${!blazeConnected ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  platform.blazeEnabled && blazeConnected
                    ? "translate-x-5"
                    : "translate-x-0"
                }`}
              />
            </button>
            <select
              disabled={!blazeConnected}
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-2 py-1.5 text-[10px] font-medium text-slate-700 shadow-inner focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
              value={platform.blazeWorkflow || workflows[0]}
              onChange={(e) => handleFieldChange("blazeWorkflow", e.target.value)}
            >
              {workflows.map((wf) => (
                <option key={wf} value={wf}>
                  {wf}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {showRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="mt-1 inline-flex items-center justify-center rounded-full border border-slate-200 px-3 py-1 text-[10px] font-medium text-slate-500 hover:bg-slate-50"
        >
          Remove
        </button>
      )}
    </div>
  );
}

const SocialMediaModule = () => {
  const [platforms, setPlatforms] = useState(
    defaultPlatforms.map((p) => ({ ...p, url: "", blazeEnabled: false }))
  );
  const [blazeConnected, setBlazeConnected] = useState(false);
  const [blazeIdentity, setBlazeIdentity] = useState("Not connected");

  const handlePlatformChange = (updatedPlatform) => {
    setPlatforms((prev) =>
      prev.map((p) => (p.id === updatedPlatform.id ? updatedPlatform : p))
    );
  };

  const handleRemovePlatform = (id) => {
    setPlatforms((prev) => prev.filter((p) => p.id !== id));
  };

  const handleAddPlatform = () => {
    const id = `custom-${Date.now()}`;
    setPlatforms((prev) => [
      ...prev,
      {
        id,
        label: "Custom Platform",
        icon: "✨",
        placeholder: "https://",
        url: "",
        blazeEnabled: false,
        isCustom: true,
      },
    ]);
  };

  const handleMockConnectBlaze = () => {
    if (!blazeConnected) {
      setBlazeConnected(true);
      setBlazeIdentity("Connected as @teletorget-demo");
    } else {
      setBlazeConnected(false);
      setBlazeIdentity("Not connected");
      setPlatforms((prev) => prev.map((p) => ({ ...p, blazeEnabled: false })));
    }
  };

  const connectedPlatforms = platforms.filter(
    (p) => p.url && p.url.trim() !== ""
  );
  const blazeEnabledCount = platforms.filter(
    (p) => p.blazeEnabled && blazeConnected
  ).length;

  return (
    <div className="min-h-screen w-full bg-slate-50/80 px-4 py-8 sm:px-6 lg:px-10">
      <div className="mx-auto flex max-w-6xl flex-col gap-6 lg:flex-row">
        {/* Left column: main configuration */}
        <div className="w-full lg:w-3/5 flex flex-col gap-4">
          <header className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-600">
              Teletorget · Client Dashboard
            </p>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              Social Media Dashboard
            </h1>
            <p className="max-w-xl text-sm text-slate-500">
              Connect your social channels, then let Blaze.ai automate posting,
              scheduling and AI-enhanced content while you keep full control in
              Teletorget.
            </p>
          </header>

          {/* Blaze posting + analytics shortcuts */}
          <section className="rounded-2xl border border-sky-200 bg-white/70 p-4 sm:p-5 shadow-sm flex flex-col gap-4">
            <h2 className="text-sm font-semibold text-slate-900">Blaze.ai Tools</h2>
            <p className="text-xs text-slate-600 max-w-md">
              Access your Blaze.ai posting dashboard or view analytics directly.
              When Blaze releases a public analytics API, this module will fetch
              real social performance (posts, reach, engagement, shares).
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <a
                href="https://app.blaze.ai/login"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center rounded-xl bg-sky-600 text-white text-xs font-semibold px-4 py-2 shadow-sm hover:bg-sky-700"
              >
                Open Blaze Posting Dashboard
              </a>

              <button
                type="button"
                disabled={!blazeConnected}
                className={`flex-1 inline-flex items-center justify-center rounded-xl text-xs font-semibold px-4 py-2 shadow-sm transition-colors ${
                  blazeConnected
                    ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : "bg-slate-200 text-slate-500 cursor-not-allowed"
                }`}
              >
                View Social Analytics (Coming Soon)
              </button>
            </div>

            <div className="mt-2 rounded-xl border border-slate-100 bg-slate-50 p-3 text-[11px] text-slate-600">
              Blaze.ai does not yet expose a public analytics API. Once
              available, this dashboard will show:
              <ul className="list-disc ml-5 mt-1 space-y-1">
                <li>Post impressions & reach</li>
                <li>Engagement (likes, comments, shares)</li>
                <li>Forwarding / reposts per platform</li>
                <li>Click-through metrics</li>
              </ul>
            </div>
          </section>

          {/* Blaze integration card */}
          <section className="rounded-2xl border border-sky-100 bg-gradient-to-r from-sky-50 via-white to-sky-50/70 p-4 sm:p-5 shadow-sm flex flex-col gap-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-600 text-white text-lg font-semibold shadow-sm">
                  BA
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">
                    Blaze.ai Integration
                  </h2>
                  <p className="text-xs text-slate-500">
                    One connection handles AI content, scheduling and
                    cross-channel publishing for all your linked profiles.
                  </p>
                  <a
                    href="https://blaze.ai/signup"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-block text-[11px] font-semibold text-sky-600 hover:text-sky-700"
                  >
                    Don’t have an account? Create your Blaze.ai subscription
                    here →
                  </a>
                  <p className="mt-1 text-[11px] font-medium text-sky-700">
                    {blazeIdentity}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleMockConnectBlaze}
                className={`inline-flex items-center justify-center rounded-xl px-4 py-2 text-xs font-semibold shadow-sm transition-colors ${
                  blazeConnected
                    ? "border border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                    : "bg-sky-600 text-white hover:bg-sky-700"
                }`}
              >
                {blazeConnected ? "Disconnect Blaze.ai" : "Connect Blaze.ai"}
              </button>
            </div>

            <div className="grid gap-3 text-[11px] text-slate-500 sm:grid-cols-3">
              <div className="flex items-start gap-2">
                <span className="mt-0.5 text-sky-500">●</span>
                <span>Centralize social publishing inside Teletorget.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-0.5 text-sky-500">●</span>
                <span>Use AI to re-write and adapt content per channel.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-0.5 text-sky-500">●</span>
                <span>Schedule posts across all connected profiles.</span>
              </div>
            </div>
          </section>

          {/* Social platforms list */}
          <section className="mt-2 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900">
                Social Media Profiles
              </h2>
              <button
                type="button"
                onClick={handleAddPlatform}
                className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
              >
                <span className="text-sm">＋</span>
                <span>Add platform</span>
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Start with the most common channels. You can add any niche or
              regional platform you use via the
              <span className="font-medium"> Add platform</span> button.
            </p>

            <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {platforms.map((platform) => (
                <SocialPlatformRow
                  key={platform.id}
                  platform={platform}
                  onChange={handlePlatformChange}
                  onRemove={() => handleRemovePlatform(platform.id)}
                  showRemove={Boolean(platform.isCustom)}
                  blazeConnected={blazeConnected}
                />
              ))}
            </div>
          </section>
        </div>

        {/* Right column: live summary / preview */}
        <aside className="w-full lg:w-2/5">
          <div className="sticky top-6 flex flex-col gap-4">
            {/* Summary card */}
            <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm flex flex-col gap-3">
              <h2 className="text-sm font-semibold text-slate-900">
                Channel Overview
              </h2>
              <p className="text-xs text-slate-500">
                Quick summary of your current setup. Use this to verify that all
                important channels are connected before enabling full
                automation.
              </p>
              <div className="grid grid-cols-2 gap-3 text-[11px] text-slate-600">
                <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 flex flex-col gap-1">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                    Connected channels
                  </span>
                  <span className="text-lg font-semibold text-slate-900">
                    {connectedPlatforms.length}
                  </span>
                  <span className="text-[11px] text-slate-500">
                    of {platforms.length} configured
                  </span>
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 flex flex-col gap-1">
                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                    Blaze automation
                  </span>
                  <span className="text-lg font-semibold text-slate-900">
                    {blazeConnected ? blazeEnabledCount : 0}
                  </span>
                  <span className="text-[11px] text-slate-500">
                    channels using Blaze.ai
                  </span>
                </div>
              </div>

              <div className="mt-2 rounded-xl border border-slate-100 bg-slate-50/80 p-3">
                <p className="text-[11px] font-medium text-slate-700 mb-1">
                  Connected profiles
                </p>
                {connectedPlatforms.length === 0 ? (
                  <p className="text-[11px] text-slate-500">
                    No profiles connected yet. Start by adding URLs on the left
                    side.
                  </p>
                ) : (
                  <ul className="space-y-1 text-[11px] text-slate-600 max-h-32 overflow-auto">
                    {connectedPlatforms.map((p) => (
                      <li
                        key={p.id}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="flex items-center gap-1.5">
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[11px] text-white">
                            {p.icon || ""}
                          </span>
                          <span className="font-medium">{p.label}</span>
                        </span>
                        <span className="truncate text-[10px] text-slate-400 max-w-[120px]">
                          {p.url}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Publishing preview card */}
            <div className="rounded-2xl border border-slate-200 bg-slate-900 text-slate-50 p-4 shadow-sm flex flex-col gap-3">
              <h2 className="text-sm font-semibold">Next publish window</h2>
              <p className="text-[11px] text-slate-300">
                This is a static preview. When the Blaze API is available, this
                box can show the next scheduled posts and which channels they
                will be pushed to.
              </p>
              <div className="mt-1 rounded-xl bg-slate-800/80 p-3 flex flex-col gap-2 text-[11px]">
                <div className="flex items-center justify-between">
                  <span className="text-slate-200 font-medium">
                    Tonight 19:30
                  </span>
                  <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">
                    Demo
                  </span>
                </div>
                <p className="text-slate-300 line-clamp-2">
                  "New blog post: How we use Teletorget + Blaze.ai to keep all
                  client channels updated without spending all day in social
                  media tools."
                </p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  <span className="rounded-full bg-slate-700 px-2 py-0.5 text-[10px]">
                    Facebook
                  </span>
                  <span className="rounded-full bg-slate-700 px-2 py-0.5 text-[10px]">
                    Instagram
                  </span>
                  <span className="rounded-full bg-slate-700 px-2 py-0.5 text-[10px]">
                    LinkedIn
                  </span>
                  <span className="rounded-full bg-slate-700 px-2 py-0.5 text-[10px]">
                    X / Twitter
                  </span>
                </div>
              </div>

              <p className="text-[10px] text-slate-400 mt-1">
                Tip: Once real scheduling is connected, this area should also
                show failed posts and soft warnings (for example, missing
                images, too much text, or blocked channels).
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default SocialMediaModule;

// Manual test cases (for reference only, not executed here):
// 1) Load component with no URLs set → all cards show "Not connected" and
//    connected count in summary is 0.
// 2) Add a URL to Facebook and Instagram → both cards show "Connected" and
//    summary connected count becomes 2.
// 3) Click "Connect Blaze.ai" → Blaze status text updates and Blaze toggle
//    switches become active.
// 4) Enable Blaze on one platform → Blaze automation count in summary shows 1.
// 5) Add custom platform via "Add platform" → new card appears with "Custom"
//    badge and removable via Remove button.
// 6) Click "Disconnect Blaze.ai" → Blaze toggles are disabled and automation
//    count resets to 0.
